import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import './index.css';
import App from './App';
import axios from 'axios';
import config from './config';
import reportWebVitals from './reportWebVitals';
import { SnackbarProvider } from 'notistack';
import { AuthProvider } from './contexts/AuthContext';
import { TourProvider } from './contexts/TourContext';
import theme from './theme';
import { SystemSettingsProvider } from './contexts/SystemSettingsContext';

// 拡張機能関連のエラーをキャッチするグローバルエラーハンドラー
window.addEventListener('error', (event) => {
  // Chrome拡張機能に関連するエラーをフィルタリング
  if (event.filename && event.filename.includes('chrome-extension://')) {
    console.warn('Chrome拡張機能に関連するエラーを検出:', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno
    });
    
    // エラーの伝播を防ぐ
    event.preventDefault();
    return false;
  }
});

// Promise拒否に関するグローバルハンドラー
window.addEventListener('unhandledrejection', (event) => {
  // Chrome拡張機能に関連する未処理の拒否をフィルタリング
  if (event.reason && event.reason.message && 
      event.reason.message.includes('chrome-extension://')) {
    console.warn('Chrome拡張機能に関連する未処理の拒否:', event.reason);
    
    // エラーの伝播を防ぐ
    event.preventDefault();
    return false;
  }
});

// Axiosのグローバル設定
axios.defaults.baseURL = config.API_BASE_URL;
axios.defaults.withCredentials = true;

// リクエスト前のインターセプター
axios.interceptors.request.use(
  (config) => {
    // トークンがある場合、ヘッダーに追加
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// レスポンス後のインターセプター
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // エラーハンドリング
    if (error.response) {
      switch (error.response.status) {
        case 401:
          // 認証エラー時の処理
          console.warn('認証エラー: トークンが無効または期限切れ');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          break;
        case 403:
          console.warn('権限エラー: アクセスが拒否されました');
          break;
        case 404:
          console.warn('リソースが見つかりません');
          break;
        case 500:
          console.error('サーバーエラー');
          break;
        default:
          console.warn('不明なエラー');
      }
    } else if (error.request) {
      // リクエストは送信されたが、レスポンスがない
      console.error('サーバーからレスポンスがありません');
    } else {
      // リクエスト送信前にエラーが発生
      console.error('リクエスト設定中にエラーが発生しました', error.message);
    }
    return Promise.reject(error);
  }
);

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <BrowserRouter>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <SystemSettingsProvider>
            <TourProvider>
              <SnackbarProvider maxSnack={3}>
                <App />
              </SnackbarProvider>
            </TourProvider>
          </SystemSettingsProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  </React.StrictMode>
);

reportWebVitals();
